import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  PlayArrow,
  PictureAsPdf,
  ExpandMore,
  Help,
  Assessment,
  TableChart,
  GetApp,
} from '@mui/icons-material';
import { apiCall } from '../utils/api';

interface CannedReport {
  id: string;
  name: string;
  description: string;
  category: string;
}

interface ReportData {
  reportInfo?: CannedReport;
  data: any[];
  generatedAt: string;
  rowCount: number;
}

interface PDFInfo {
  filename: string;
  size: number;
  downloadUrl: string;
}

interface ReportResults {
  success: boolean;
  reportData: ReportData;
  pdf?: PDFInfo;
  file?: PDFInfo; // Can reuse PDFInfo interface for other file types
  format?: string;
}

interface DatabaseColumn {
  column_name: string;
  data_type: string;
  is_nullable: string;
  column_default: string | null;
  column_comment: string | null;
}

interface DatabaseTable {
  comment: string | null;
  columns: DatabaseColumn[];
}

interface DatabaseSchema {
  [tableName: string]: DatabaseTable;
}

const AdminReports: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [cannedReports, setCannedReports] = useState<CannedReport[]>([]);
  const [customQuery, setCustomQuery] = useState('');
  const [reportResults, setReportResults] = useState<ReportResults | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [schemaOpen, setSchemaOpen] = useState(false);
  const [databaseSchema, setDatabaseSchema] = useState<DatabaseSchema>({});
  const [reportFormat, setReportFormat] = useState('json');

  useEffect(() => {
    loadCannedReports();
    loadDatabaseSchema();
  }, []);

  const loadCannedReports = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await apiCall('/api/admin/reports/canned', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setCannedReports(data.reports);
        }
      }
    } catch (error) {
      console.error('Error loading canned reports:', error);
      setError('Failed to load canned reports');
    }
  };

  const loadDatabaseSchema = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await apiCall('/api/admin/reports/schema', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setDatabaseSchema(data.schema);
        }
      }
    } catch (error) {
      console.error('Error loading database schema:', error);
    }
  };

  const executeCannedReport = async (reportId: string) => {
    setLoading(true);
    setError('');
    
    try {
      const token = localStorage.getItem('token');
      const response = await apiCall(`/api/admin/reports/canned/${reportId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ format: reportFormat })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setReportResults(data);
        // Don't auto-download - let user click download button if they want a file
      } else {
        setError(data.message || 'Failed to execute report');
      }
    } catch (error: any) {
      console.error('Error executing canned report:', error);
      setError('Failed to execute report');
    } finally {
      setLoading(false);
    }
  };

  const executeCustomQuery = async () => {
    if (!customQuery.trim()) {
      setError('Please enter a SQL query');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      const token = localStorage.getItem('token');
      
      // Validate query first
      const validateResponse = await apiCall('/api/admin/reports/validate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ query: customQuery })
      });
      
      const validateData = await validateResponse.json();
      
      if (!validateData.success) {
        setError(validateData.message);
        return;
      }

      // Execute query
      const response = await apiCall('/api/admin/reports/custom', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ query: customQuery, format: reportFormat })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setReportResults(data);
        // Don't auto-download - let user click download button if they want a file
      } else {
        setError(data.message || 'Failed to execute query');
      }
    } catch (error: any) {
      console.error('Error executing custom query:', error);
      setError('Failed to execute query');
    } finally {
      setLoading(false);
    }
  };

  const downloadFile = async (downloadUrl: string, filename: string) => {
    try {
      const token = localStorage.getItem('token');
      const baseUrl = process.env.REACT_APP_API_BASE_URL?.replace('/api', '') || 
        (process.env.NODE_ENV === 'development' ? 'http://localhost:3001' : '');
      const response = await fetch(`${baseUrl}${downloadUrl}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        throw new Error('Failed to download file');
      }
    } catch (error) {
      console.error('Download failed:', error);
      setError('Failed to download file');
    }
  };

  const renderReportData = (data: any[]) => {
    if (!data || data.length === 0) {
      return <Typography>No data found</Typography>;
    }

    const columns = Object.keys(data[0]);
    
    return (
      <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell key={column} sx={{ fontWeight: 'bold' }}>
                  {column.replace(/_/g, ' ').toUpperCase()}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((row: any, index: number) => (
              <TableRow key={index} hover>
                {columns.map((column) => (
                  <TableCell key={column}>
                    {formatCellValue(row[column])}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const formatCellValue = (value: any) => {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'object' && value instanceof Date) {
      return new Date(value).toLocaleDateString();
    }
    if (typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}/)) {
      return new Date(value).toLocaleDateString();
    }
    return String(value);
  };

  const renderCannedReports = () => {
    const categories = Array.from(new Set(cannedReports.map(r => r.category)));
    
    return (
      <Box>
        {/* Format Selection for Canned Reports */}
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h6">Output Format:</Typography>
          <TextField
            select
            label="Format"
            value={reportFormat}
            onChange={(e) => setReportFormat(e.target.value)}
            size="small"
            sx={{ minWidth: 120 }}
          >
            <MenuItem value="json">JSON</MenuItem>
            <MenuItem value="pdf">PDF</MenuItem>
            <MenuItem value="csv">CSV</MenuItem>
            <MenuItem value="xlsx">Excel</MenuItem>
          </TextField>
        </Box>

        {categories.map((category) => (
          <Box key={category} sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              {category} Reports
            </Typography>
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, 
              gap: 2 
            }}>
              {cannedReports
                .filter(r => r.category === category)
                .map((report) => (
                  <Card key={report.id}>
                    <CardContent>
                      <Typography variant="subtitle1" gutterBottom>
                        {report.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {report.description}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<PlayArrow />}
                          onClick={() => executeCannedReport(report.id)}
                          disabled={loading}
                        >
                          Run Report
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
            </Box>
          </Box>
        ))}
      </Box>
    );
  };

  const renderCustomQuery = () => {
    return (
      <Box>
        <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
          <Button
            variant="outlined"
            startIcon={<Help />}
            onClick={() => setSchemaOpen(true)}
          >
            View Schema
          </Button>
          <TextField
            select
            label="Output Format"
            value={reportFormat}
            onChange={(e) => setReportFormat(e.target.value)}
            size="small"
            sx={{ minWidth: 120 }}
          >
            <MenuItem value="json">JSON</MenuItem>
            <MenuItem value="pdf">PDF</MenuItem>
            <MenuItem value="csv">CSV</MenuItem>
            <MenuItem value="xlsx">Excel</MenuItem>
          </TextField>
        </Box>

        <TextField
          multiline
          rows={8}
          fullWidth
          label="SQL Query"
          value={customQuery}
          onChange={(e) => setCustomQuery(e.target.value)}
          placeholder="SELECT * FROM users WHERE created_at >= '2024-01-01'"
          sx={{ mb: 2, fontFamily: 'monospace' }}
          helperText="Only SELECT queries are allowed. No INSERT, UPDATE, DELETE, etc."
        />

        <Button
          variant="contained"
          startIcon={<PlayArrow />}
          onClick={executeCustomQuery}
          disabled={loading || !customQuery.trim()}
        >
          Execute Query
        </Button>
      </Box>
    );
  };

  const renderResults = () => {
    if (!reportResults) return null;

    const { reportData, pdf, file, format } = reportResults;
    
    return (
      <Box sx={{ mt: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Report Results</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {pdf && (
              <Button
                variant="outlined"
                startIcon={<PictureAsPdf />}
                onClick={() => downloadFile(pdf.downloadUrl, pdf.filename)}
              >
                Download PDF ({Math.round(pdf.size / 1024)} KB)
              </Button>
            )}
            {file && (
              <Button
                variant="outlined"
                startIcon={format === 'xlsx' ? <TableChart /> : <GetApp />}
                onClick={() => downloadFile(file.downloadUrl, file.filename)}
              >
                Download {format?.toUpperCase()} ({Math.round(file.size / 1024)} KB)
              </Button>
            )}
          </Box>
        </Box>

        {reportData.reportInfo && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <strong>{reportData.reportInfo.name}</strong>: {reportData.reportInfo.description}
          </Alert>
        )}

        <Typography variant="body2" color="text.secondary" gutterBottom>
          Generated: {new Date(reportData.generatedAt).toLocaleString()} | 
          Records: {reportData.rowCount} | 
          Format: {format?.toUpperCase() || 'JSON'}
        </Typography>

        {renderReportData(reportData.data)}
      </Box>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        <Assessment sx={{ mr: 1, verticalAlign: 'middle' }} />
        Admin Reports
      </Typography>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab label="Canned Reports" />
          <Tab label="Custom Query" />
        </Tabs>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
          <CircularProgress />
        </Box>
      )}

      {activeTab === 0 && renderCannedReports()}
      {activeTab === 1 && renderCustomQuery()}
      
      {renderResults()}

      {/* Database Schema Dialog */}
      <Dialog
        open={schemaOpen}
        onClose={() => setSchemaOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Database Schema Reference</DialogTitle>
        <DialogContent>
          {Object.entries(databaseSchema).map(([tableName, tableInfo]) => (
            <Accordion key={tableName}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="subtitle1">
                  <TableChart sx={{ mr: 1, verticalAlign: 'middle' }} />
                  {tableName}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Column</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Nullable</TableCell>
                      <TableCell>Comment</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tableInfo.columns.map((column) => (
                      <TableRow key={column.column_name}>
                        <TableCell>{column.column_name}</TableCell>
                        <TableCell>{column.data_type}</TableCell>
                        <TableCell>
                          <Chip 
                            label={column.is_nullable === 'YES' ? 'Yes' : 'No'}
                            size="small"
                            color={column.is_nullable === 'YES' ? 'default' : 'primary'}
                          />
                        </TableCell>
                        <TableCell>{column.column_comment || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </AccordionDetails>
            </Accordion>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSchemaOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminReports;